import classNames from 'classnames';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import Badge from '@/modules/common/components/Badge.tsx';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { IncidentOptionValue } from '@/modules/common/components/modals/CreateIncidentModal.tsx';
import IncidentCreatedModal from '@/modules/common/components/modals/IncidentCreatedModal.tsx';
import {
  IncidentInfoModalProps,
  TabNames,
} from '@/modules/common/components/modals/IncidentInfoModal.tsx';
import { IncidentReportingState } from '@/modules/common/pages/IncidentReporting.tsx';
import { commonPaths } from '@/modules/common/routes.tsx';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import {
  useDeleteIncidentMutation,
  useResolveIncidentMutation,
  useRestoreIncidentMutation,
} from '@/modules/admin-dashboard/queries/incidents.ts';
import { Avatar, Button, ConfirmModal, DropdownMenu, ModalProviderContext, SelectOption } from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import {
  IncidentResultStatus,
  IncidentResultWithIncidentTypeRelResponse,
  IncidentTypeWithRelationsResponse,
} from 'compliance-api';

interface IncidentItemProps {
  setIncidentInfo: Dispatch<
    SetStateAction<null | Pick<IncidentInfoModalProps, 'incident' | 'defaultActiveTab'>>
  >;
  isUserAdmin: boolean;
  incident: IncidentResultWithIncidentTypeRelResponse;
  responsibleDepartmentId: number;
  incidentType: IncidentTypeWithRelationsResponse;
  clientId?: number;
}

const getContentClassByUserRole = (isAdmin: boolean) => {
  return isAdmin
    ? 'grid-cols-[2fr_auto] md:grid-cols-[1fr_2fr_auto_auto]'
    : 'grid-cols-1 md:grid-cols-3';
};

const IncidentItem = ({
  isUserAdmin,
  incident,
  responsibleDepartmentId,
  setIncidentInfo,
  incidentType,
  clientId,
}: IncidentItemProps) => {
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const {
    name,
    date_occurred,
    status,
    id,
    incident_id,
    user,
    deleted_at,
    reference_name,
    created_with_ai,
  } = incident;

  const { data } = useCurrentUserQuery();

  const { mutateAsync: resolveIncident, isLoading: isResolvingIncident } =
    useResolveIncidentMutation({
      onSuccess: () => {
        openModal(IncidentCreatedModal, { isCreatedEvent: false });
      },
    });

  const { mutateAsync: deleteIncident } = useDeleteIncidentMutation();

  const isMyDepartmentResponsible = (data?.departments || []).some(
    ({ id }) => id === responsibleDepartmentId,
  );

  const handleResolveIncident = useCallback(() => {
    if (incidentType?.completion_form_id) {
      const state: IncidentReportingState & {
        incidentOption?: SelectOption<IncidentOptionValue>;
        clientOption?: SelectOption<number>;
      } = {
        incident_id: incident.incident_id,
        form_id: incidentType?.completion_form_id,
        departmentName: incidentType.department?.name || '',
        client_id: clientId,
        date_occurred: dayjs().format('YYYY-MM-DDTHH:mm'),
        name: incident?.name || '',
        returnPath: location.pathname + location.search,
        incident_result_id: incident.id,
        completion_form_id: incidentType?.completion_form_id,
      };

      navigate(
        { pathname: commonPaths.static.incidentReporting, search: location.search },
        {
          state: state,
          replace: false,
        },
      );

      return;
    }
    resolveIncident({ id, incident_id });
  }, [
    incidentType?.completion_form_id,
    incidentType.department?.name,
    resolveIncident,
    id,
    incident_id,
    incident.incident_id,
    incident?.name,
    clientId,
    navigate,
  ]);

  const options = useMemo(() => {
    const res = [
      // {
      //   label: 'History',
      //   onClick: () => setIncidentInfo({ defaultActiveTab: TabNames.History, incident }),
      // },
      {
        label: 'Linked tasks',
        onClick: () => setIncidentInfo({ defaultActiveTab: TabNames.LinkedTasks, incident }),
      },
    ];

    if (isUserAdmin) {
      res.unshift({
        label: 'Archive',
        onClick: () => {
          openModal(ConfirmModal, {
            title: 'Archive event?',
            onConfirm: async () => {
              deleteIncident({ id });
            },
          });
        },
      });
    }

    return res;
  }, [id, incident, isUserAdmin, deleteIncident, openModal, setIncidentInfo]);

  return (
    <div
      className={classNames('pr-4 pl-10 ', {
        'bg-gray-100': deleted_at,
      })}
    >
      <div className={classNames('flex gap-6 py-4 [&:not(:last-child)]:border-b items-center')}>
        <div className="flex flex-col w-1/3">
          <span
            className="hover:underline cursor-pointer text-sm"
            onClick={setIncidentInfo.bind(null, { incident })}
          >
            {name}
          </span>
          <div className="flex items-center text-neutral-500 text-xs">
            {dayjs(date_occurred).format('DD-MM-YYYY, HH:mm')} | {reference_name} |
            <CreatedByAiLabel created_with_ai={created_with_ai} />
          </div>
        </div>
        <div className="w-2/3 flex justify-end">
          <div
            className={classNames(
              'w-full max-w-[450px] grid gap-4 items-center',
              getContentClassByUserRole(isUserAdmin || isMyDepartmentResponsible),
            )}
          >
            <div className="min-w-[65px]">
              <Badge
                type={status === IncidentResultStatus.Open ? 'critical' : 'success'}
                className="text-white w-fit capitalize text-xs hidden md:flex"
              >
                {status}
              </Badge>
            </div>

            <div className="flex items-center gap-3">
              <Avatar firstName={user.first_name} lastName={user.last_name} src={user.avatar_url} />
              <div className="flex flex-col">
                <span className="text-neutral-500 text-xs">Reported by</span>
                <span className="text-sm">
                  {user?.first_name} {user?.last_name}
                </span>
              </div>
            </div>

            {(isUserAdmin || isMyDepartmentResponsible) && !deleted_at && (
              <Button
                className="w-fit h-fit"
                disabled={isResolvingIncident || status !== IncidentResultStatus.Open}
                onClick={handleResolveIncident}
              >
                Resolve
              </Button>
            )}
            {deleted_at ? (
              <ArchivedItemAction id={id} mutation={useRestoreIncidentMutation} />
            ) : (
              <div className="hidden md:block">
                <DropdownMenu
                  items={options}
                  button={
                    <button className="flex items-center justify-center">
                      <img alt="actions" src={DotsHorizontalIcon} />
                    </button>
                  }
                  className="w-[180px] divide-white"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentItem;
